import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  CreditCard,
  CheckCircle2,
  Ban,
  Clock,
  AlertTriangle,
  ChevronRight,
  Smartphone,
  RefreshCw,
  DollarSign,
  TrendingUp,
  PieChart,
  X,
} from '@/lib/icons';
import { ParAgingCard } from '@/components/dashboard/ParAgingCard';
import {
  AGING_BUCKETS,
  bucketOf,
  isExposed,
  type AgingBucketKey,
} from '@/lib/par-aging';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatCurrency } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import api, { handleApiResponse } from '@/lib/api';
import { OperationResult } from '@/lib/operation-result';
import type { LoanStatus } from '@/types';
import {
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/portfolio/')({
  component: PortfolioPage,
});

/* ── Map backend contract → loan-like shape ──────── */
interface LoanItem {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  deviceModel: string;
  principal: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: LoanStatus;
  repaymentProgress: number;
  daysOverdue: number;
  overdueAmount: number;
  nextPaymentDate: string;
  nextPaymentAmount: number;
}

function mapContractToLoan(c: any): LoanItem {
  const totalAmount = Number(c.totalAmount ?? 0);
  const paidAmount = totalAmount - Number(c.remainingBalance ?? totalAmount);
  const remainingAmount = Number(c.remainingBalance ?? 0);
  const progress =
    totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

  // Map backend status to UI status
  let status: LoanStatus = 'pending';
  const rawStatus = c.status?.toLowerCase() || '';
  if (rawStatus === 'active') status = 'active';
  else if (rawStatus === 'completed') status = 'completed';
  else if (rawStatus === 'defaulted') status = 'defaulted';
  else if (rawStatus === 'cancelled') status = 'completed';

  // The backend derives these from the live payment schedule; recomputing
  // them here from contract dates only ever disagreed with the contract page.
  const daysOverdue = Number(c.daysOverdue ?? 0);
  // A business calendar date ("YYYY-MM-DD"); never round-trip it through Date.
  const nextPaymentDate = c.nextPaymentDate ?? '';

  // Backend list response exposes `deviceName` (e.g. "Samsung Galaxy A14");
  // older mappings used `deviceModel`, which the list endpoint never sends.
  const deviceLabel =
    c.deviceName ||
    [c.deviceBrand, c.deviceModel].filter(Boolean).join(' ') ||
    (c.deviceId ? `Device #${String(c.deviceId).slice(-6)}` : 'Device');

  return {
    id: c.id || '',
    customerId: c.customerId || '',
    customerName: c.customerName || `Customer #${c.customerId ?? ''}`,
    customerPhone: c.customerPhone || '',
    deviceModel: deviceLabel,
    principal: Number(
      c.financedPrincipal ??
        Math.max(Number(c.devicePrice ?? 0) - Number(c.downPayment ?? 0), 0)
    ),
    totalAmount,
    paidAmount,
    remainingAmount,
    status,
    repaymentProgress: progress,
    daysOverdue,
    overdueAmount: Number(c.overdueAmount ?? 0),
    nextPaymentDate,
    nextPaymentAmount: Number(c.nextPaymentAmount ?? 0),
  };
}

async function fetchLoans(): Promise<LoanItem[]> {
  const result = await handleApiResponse<any[]>(() =>
    api.get<OperationResult<any[]>>('/v1/contracts/', {
      params: { limit: 1000 },
    })
  );
  if (result.succeeded && result.value) {
    return result.value.map(mapContractToLoan);
  }
  return [];
}

function getStatusConfig(status: LoanStatus) {
  const config = {
    pending: {
      label: 'Pending',
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      icon: <Clock className="h-3 w-3" />,
    },
    active: {
      label: 'Active',
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    completed: {
      label: 'Completed',
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    defaulted: {
      label: 'Defaulted',
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      icon: <Ban className="h-3 w-3" />,
    },
    restructured: {
      label: 'Restructured',
      cls: 'bg-purple-500/15 text-purple-600 border-purple-500/30 dark:text-purple-400',
      icon: <RefreshCw className="h-3 w-3" />,
    },
  };
  return config[status];
}

function PortfolioPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LoanStatus | 'all'>('all');
  const [agingFilter, setAgingFilter] = useState<AgingBucketKey | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const { data: loans = [], isLoading } = useQuery({
    queryKey: ['portfolio'],
    queryFn: fetchLoans,
  });

  const filtered = loans.filter((l) => {
    const matchesSearch =
      l.customerName.toLowerCase().includes(search.toLowerCase()) ||
      l.customerPhone.includes(search) ||
      l.deviceModel.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchesAging =
      agingFilter === null ||
      (isExposed(l) && bucketOf(l.daysOverdue) === agingFilter);
    return matchesSearch && matchesStatus && matchesAging;
  });

  const totalPrincipal = loans.reduce((s, l) => s + l.principal, 0);
  const totalOutstanding = loans.reduce((s, l) => s + l.remainingAmount, 0);
  const avgProgress =
    loans.length > 0
      ? Math.round(
          loans.reduce((s, l) => s + l.repaymentProgress, 0) / loans.length
        )
      : 0;
  const activeCount = loans.filter((l) => l.status === 'active').length;

  const stats = [
    {
      label: 'Financed Principal',
      value: formatCurrency(totalPrincipal, currency),
      icon: DollarSign,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Outstanding',
      value: formatCurrency(totalOutstanding, currency),
      icon: TrendingUp,
      color: 'from-amber-500 to-amber-600',
    },
    {
      label: 'Avg. Repayment',
      value: `${avgProgress}%`,
      icon: PieChart,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: 'Active Loans',
      value: activeCount,
      icon: CreditCard,
      color: 'from-violet-500 to-violet-600',
    },
  ];

  const statusSlices = [
    {
      name: 'Active',
      value: loans.filter((l) => l.status === 'active').length,
      fill: '#10b981',
    },
    {
      name: 'Completed',
      value: loans.filter((l) => l.status === 'completed').length,
      fill: '#3b82f6',
    },
    {
      name: 'Defaulted',
      value: loans.filter((l) => l.status === 'defaulted').length,
      fill: '#ef4444',
    },
    {
      name: 'Pending',
      value: loans.filter((l) => l.status === 'pending').length,
      fill: '#f59e0b',
    },
    {
      name: 'Restructured',
      value: loans.filter((l) => l.status === 'restructured').length,
      fill: '#8b5cf6',
    },
  ].filter((d) => d.value > 0);

  const filters = [
    { label: 'All', value: 'all' as const },
    { label: 'Active', value: 'active' as const },
    { label: 'Completed', value: 'completed' as const },
    { label: 'Defaulted', value: 'defaulted' as const },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Loan Portfolio
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Active lending portfolio & Murabaha repayment tracking
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Loan Portfolio</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Active lending portfolio & Murabaha repayment tracking
          </p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-lg">
          <CreditCard className="h-5 w-5" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row — PAR Aging + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ParAgingCard
          loans={loans}
          currency={currency}
          activeBucket={agingFilter}
          onBucketSelect={(bucket) => {
            setAgingFilter(bucket);
            if (bucket) {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }}
        />

        {/* Status Distribution Donut */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Loan Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPieChart>
                <Pie
                  data={statusSlices}
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusSlices.map((d) => (
                    <Cell key={d.name} fill={d.fill} />
                  ))}
                </Pie>
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '12px' }}
                />
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card ref={tableRef} className="shadow-md border-0 scroll-mt-4">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-lg mr-2">Loans</CardTitle>
              {filters.map((f) => (
                <Button
                  key={f.label}
                  variant={statusFilter === f.value ? 'default' : 'outline'}
                  size="sm"
                  className={
                    statusFilter === f.value
                      ? 'bg-primary text-primary-foreground'
                      : ''
                  }
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                </Button>
              ))}
              {agingFilter && (
                <Badge
                  variant="outline"
                  className="gap-1 pl-2 pr-1 py-1 font-normal"
                >
                  Aging:{' '}
                  {AGING_BUCKETS.find((b) => b.key === agingFilter)?.label}
                  <button
                    type="button"
                    aria-label="Clear aging filter"
                    onClick={() => setAgingFilter(null)}
                    className="rounded-sm p-0.5 hover:bg-muted"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer, phone, or device..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No loans found</p>
              <p className="text-sm">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden sm:table-cell">
                      Device
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      Total
                    </TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Next Payment
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-10">
                      <span className="sr-only">Open contract</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((loan) => {
                    const cfg = getStatusConfig(loan.status);
                    const initials = loan.customerName
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase();
                    const colors = [
                      'bg-blue-500',
                      'bg-emerald-500',
                      'bg-violet-500',
                      'bg-amber-500',
                      'bg-rose-500',
                    ];
                    const idx = loan.customerName.charCodeAt(0) % colors.length;
                    // A loan row is a view onto its contract, so `loan.id` is
                    // the contract UUID. The mapper falls back to '' when the
                    // backend omits it — such a row stays inert rather than
                    // routing to a broken detail page.
                    const contractId = loan.id;
                    return (
                      <TableRow
                        key={loan.id}
                        // Mouse convenience only — the customer-name link below
                        // is the real keyboard/assistive-tech affordance, so the
                        // row stays non-focusable and table semantics hold.
                        onClick={
                          contractId
                            ? (e) => {
                                // Let nested links/buttons handle their own clicks.
                                if (
                                  (e.target as HTMLElement).closest('a,button')
                                )
                                  return;
                                navigate({
                                  to: '/contracts/$contractId',
                                  params: { contractId },
                                });
                              }
                            : undefined
                        }
                        className={cn(
                          'transition-colors group',
                          contractId
                            ? 'hover:bg-muted/30 cursor-pointer'
                            : 'cursor-default'
                        )}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-full ${colors[idx]} flex items-center justify-center text-white text-xs font-bold shrink-0`}
                            >
                              {initials}
                            </div>
                            <div className="min-w-0">
                              {contractId ? (
                                <Link
                                  to="/contracts/$contractId"
                                  params={{ contractId }}
                                  className="rounded-sm font-medium transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background group-hover:text-primary"
                                >
                                  {loan.customerName}
                                </Link>
                              ) : (
                                <div className="font-medium">
                                  {loan.customerName}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground">
                                {loan.customerPhone}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Smartphone className="h-3.5 w-3.5" />
                            <span className="text-sm">{loan.deviceModel}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div>
                            <div className="font-semibold">
                              {formatCurrency(loan.totalAmount, currency)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(loan.remainingAmount, currency)}{' '}
                              left
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all',
                                  loan.repaymentProgress >= 60
                                    ? 'bg-emerald-500'
                                    : loan.repaymentProgress >= 30
                                      ? 'bg-amber-500'
                                      : 'bg-red-500'
                                )}
                                style={{ width: `${loan.repaymentProgress}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium w-8 text-right">
                              {loan.repaymentProgress}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {loan.daysOverdue > 0 ? (
                            <span className="text-red-500 flex items-center gap-1 text-sm">
                              <AlertTriangle className="h-3 w-3" />
                              {loan.daysOverdue}d overdue
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {formatDate(loan.nextPaymentDate, '–')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.cls}>
                            {cfg.icon}
                            <span className="ml-1">{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* Decorative — the customer-name link is the real
                              control, so this must not be announced twice. */}
                          {contractId && (
                            <ChevronRight
                              aria-hidden="true"
                              className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-primary"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
